import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addMonths, addWeeks, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, format, isSameMonth, isSameYear, isToday, isWeekend,
} from 'date-fns';
import { fr as frLocale, enCA as enLocale } from 'date-fns/locale';

import Icon from '../../components/AppIcon';
import useTranslation from '../../hooks/useTranslation';
import StatsCard from '../../components/ui/StatsCard';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { projectService } from '../../services/projectService';
import { listSchedulableUsers } from '../../services/userManagementService';
import organizationTradesService from '../../services/organizationTradesService';
import { listAssignments } from '../../services/workerAssignmentsService';
import { formatDateWeekday } from '../../utils/formatDate';
import AssignmentModal, { shortTime } from './components/AssignmentModal';
import WeekView from './components/WeekView';

/**
 * LA COULEUR IDENTIFIE LE CHANTIER — jamais le metier, jamais l'urgence.
 *
 * C'est ce qui permet de lire d'un coup d'oeil « lundi, presque tout le monde est
 * au Beau-Sejour ». Huit teintes distinctes et lisibles, puis on boucle : au-dela,
 * l'oeil ne distingue plus, et un mois affiche rarement plus de huit chantiers.
 * Chaque teinte porte un fond pastel, un texte fonce de la MEME famille, et une
 * barre laterale saturee.
 */
const PROJECT_COLORS = [
  { bar: '#FF6B2C', bg: '#FFF1E8', text: '#9A3B0C', dot: '#FF6B2C' },
  { bar: '#2563EB', bg: '#EFF4FF', text: '#1E3A8A', dot: '#2563EB' },
  { bar: '#059669', bg: '#ECFDF5', text: '#065F46', dot: '#059669' },
  { bar: '#7C3AED', bg: '#F5F1FE', text: '#5B21B6', dot: '#7C3AED' },
  { bar: '#DB2777', bg: '#FDF2F8', text: '#9D174D', dot: '#DB2777' },
  { bar: '#0891B2', bg: '#ECFEFF', text: '#155E75', dot: '#0891B2' },
  { bar: '#CA8A04', bg: '#FEFCE8', text: '#854D0E', dot: '#CA8A04' },
  { bar: '#475569', bg: '#F1F5F9', text: '#1E293B', dot: '#475569' },
];

/**
 * LA TEINTE D'UN CHANTIER — une seule fonction, trois appelants.
 *
 * 🔴 DETERMINISTE, et volontairement independante de ce qui est a l'ecran.
 *
 * L'index venait de la liste des projets VISIBLES dans la periode chargee. Les
 * vues mois et semaine ne couvrent pas les memes dates, donc pas les memes
 * projets : les index glissaient et les couleurs changeaient sous les yeux de
 * l'utilisateur. Changer de mois, ou supprimer la derniere assignation d'un
 * chantier, produisait le meme glissement.
 *
 * Le rang vient desormais de la liste COMPLETE des projets de l'organisation,
 * triee par numero — la meme quelle que soit la vue, la periode, le filtre, ou
 * ce qu'on vient de supprimer.
 *
 * Un RANG plutot qu'un hachage de l'identifiant : deux chantiers voisins dans la
 * liste recoivent des teintes voisines mais DISTINCTES, la ou un hachage peut
 * donner deux fois la meme couleur a deux chantiers actifs cote a cote.
 */
const projectColorOf = (projectId, rankById) =>
  PROJECT_COLORS[(rankById?.[projectId] ?? 0) % PROJECT_COLORS.length];

/** Date nue AAAA-MM-JJ, sans passer par UTC — `toISOString()` reculerait d'un jour. */
const toISODate = (d) => format(d, 'yyyy-MM-dd');

/** « Michel Tremblay » -> « M. Tremblay ». Le prenom entier ne tient pas dans une puce. */
const shortName = (fullName) => {
  const parts = String(fullName || '')?.trim()?.split(/\s+/);
  if (parts?.length < 2) return parts?.[0] || '';
  return `${parts?.[0]?.[0]}. ${parts?.slice(1)?.join(' ')}`;
};

const TeamSchedulePage = () => {
  const { t, language } = useTranslation();
  const dateLocale = language === 'fr' ? frLocale : enLocale;

  // 'month' | 'week'. Volontairement NON memorise : on repart du mois a chaque visite.
  const [viewMode, setViewMode] = useState('month');
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [assignments, setAssignments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterProject, setFilterProject] = useState('all');
  const [filterTrade, setFilterTrade] = useState('all');

  const [modal, setModal] = useState(null);       // { assignment, defaultDate } | null
  const [dayDetail, setDayDetail] = useState(null); // ISO date | null

  // ── La grille : lundi -> dimanche, 5 ou 6 rangees selon le mois ─────────────
  const days = useMemo(() => {
    if (viewMode === 'week') {
      const s = startOfWeek(cursor, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: s, end: endOfWeek(cursor, { weekStartsOn: 1 }) });
    }
    const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [cursor, viewMode]);

  const rangeFrom = days?.[0] ? toISODate(days[0]) : null;
  const rangeTo = days?.[days?.length - 1] ? toISODate(days[days.length - 1]) : null;

  // ── Chargements ────────────────────────────────────────────────────────────
  const fetchAssignments = useCallback(async () => {
    if (!rangeFrom || !rangeTo) return;
    setLoading(true);
    const rows = await listAssignments(rangeFrom, rangeTo);
    setAssignments(rows || []);
    setLoading(false);
  }, [rangeFrom, rangeTo]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  useEffect(() => {
    (async () => {
      // Travailleurs ET surintendants : un surintendant est sur le chantier, sa
      // presence se planifie a la journee comme celle d'un travailleur.
      const { data } = await listSchedulableUsers();
      setWorkers(data || []);
    })();
    projectService?.getAll()?.then((rows) => setProjects(rows || []))?.catch(console.error);
    organizationTradesService?.getByOrg()?.then((rows) => setTrades(rows || []))?.catch(console.error);
  }, []);

  // ── Resolution en memoire : le service ne joint rien ────────────────────────
  const workerMap = useMemo(() => {
    const m = {};
    (workers || [])?.forEach((w) => { m[w?.id] = w; });
    return m;
  }, [workers]);

  const projectMap = useMemo(() => {
    const m = {};
    (projects || [])?.forEach((p) => { m[p?.id] = p; });
    return m;
  }, [projects]);

  /**
   * Le rang de chaque projet dans la liste COMPLETE de l'organisation, triee par
   * numero. La liste des projets est deja chargee pour le filtre « Tous les projets » :
   * aucune requete de plus. Le tri est numerique, pour que P26-4 precede P26-10.
   */
  const projectRank = useMemo(() => {
    const rank = {};
    [...(projects || [])]
      ?.sort((a, b) => String(a?.projectNumber || '')?.localeCompare(String(b?.projectNumber || ''), 'fr', { numeric: true }))
      ?.forEach((p, i) => { rank[p?.id] = i; });
    return rank;
  }, [projects]);

  /** L'unique accesseur de couleur. Ne depend d'AUCUNE liste filtree. */
  const colorOf = useCallback((projectId) => projectColorOf(projectId, projectRank), [projectRank]);

  // ── Filtres ────────────────────────────────────────────────────────────────
  const visibleAssignments = useMemo(() => (assignments || [])?.filter((a) => {
    if (filterProject !== 'all' && a?.projectId !== filterProject) return false;
    if (filterTrade !== 'all' && workerMap?.[a?.workerId]?.trade_id !== filterTrade) return false;
    return true;
  }), [assignments, filterProject, filterTrade, workerMap]);

  const byDate = useMemo(() => {
    const m = {};
    (visibleAssignments || [])?.forEach((a) => {
      (m[a?.workDate] = m[a?.workDate] || [])?.push(a);
    });
    return m;
  }, [visibleAssignments]);

  // ── Les quatre tuiles ──────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const monthOnly = (visibleAssignments || [])?.filter((a) => isSameMonth(new Date(`${a?.workDate}T12:00:00`), cursor));
    const assignedWorkers = new Set(monthOnly?.map((a) => a?.workerId));
    // Jour-personne : la duree reelle, sur une base de 8 h. Une demi-journee vaut
    // 0,5, une journee 1, et 6 h valent 0,75 — il n'y a plus de bloc a interroger.
    const minutes = (a) => {
      const toMin = (x) => { const [h, m] = String(x || '')?.split(':'); return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0); };
      return Math.max(0, toMin(a?.endTime) - toMin(a?.startTime));
    };
    const personDays = monthOnly?.reduce((s, a) => s + minutes(a) / 480, 0);
    const coveredProjects = new Set(monthOnly?.map((a) => a?.projectId));

    const weekStart = toISODate(startOfWeek(new Date(), { weekStartsOn: 1 }));
    const weekEnd = toISODate(endOfWeek(new Date(), { weekStartsOn: 1 }));
    const assignedThisWeek = new Set(
      (assignments || [])
        ?.filter((a) => a?.workDate >= weekStart && a?.workDate <= weekEnd)
        ?.map((a) => a?.workerId)
    );
    const unassigned = (workers || [])?.filter((w) => !assignedThisWeek?.has(w?.id))?.length;

    return {
      workers: assignedWorkers?.size,
      personDays: Number.isInteger(personDays) ? personDays : personDays?.toFixed(1),
      projects: coveredProjects?.size,
      unassigned,
    };
  }, [visibleAssignments, assignments, workers, cursor]);

  /** Les projets presents dans le mois, pour la legende. */
  const legend = useMemo(() => {
    const ids = [...new Set((visibleAssignments || [])?.map((a) => a?.projectId))];
    return ids?.map((id) => ({
      id,
      label: projectMap?.[id]?.projectNumber || projectMap?.[id]?.name || '—',
      color: colorOf(id),
    }));
  }, [visibleAssignments, projectMap, colorOf]);

  /** « 7:00-15:00 » — l'heure reelle, sans zero inutile. */
  const timeLabel = (a) => `${shortTime(a?.startTime)}-${shortTime(a?.endTime)}`;

  // Les fleches avancent d'un mois ou d'une semaine, selon la vue.
  const step = (n) => setCursor((c) => (viewMode === 'week' ? addWeeks(c, n) : addMonths(c, n)));
  const goToday = () => setCursor(viewMode === 'week' ? startOfWeek(new Date(), { weekStartsOn: 1 }) : startOfMonth(new Date()));

  /**
   * « septembre 2026 » en vue mois ; « 14 – 20 septembre 2026 » en vue semaine,
   * et « 28 sept. – 4 oct. 2026 » quand la semaine chevauche deux mois.
   */
  const periodLabel = useMemo(() => {
    if (viewMode !== 'week') return format(cursor, 'LLLL yyyy', { locale: dateLocale });
    const a = days?.[0];
    const b = days?.[6];
    if (!a || !b) return '';
    if (isSameMonth(a, b)) return `${format(a, 'd', { locale: dateLocale })} – ${format(b, 'd MMMM yyyy', { locale: dateLocale })}`;
    if (isSameYear(a, b)) return `${format(a, 'd MMM', { locale: dateLocale })} – ${format(b, 'd MMM yyyy', { locale: dateLocale })}`;
    return `${format(a, 'd MMM yyyy', { locale: dateLocale })} – ${format(b, 'd MMM yyyy', { locale: dateLocale })}`;
  }, [viewMode, cursor, days, dateLocale]);

  const handleSaved = () => { setModal(null); fetchAssignments(); };
  const handleDeleted = () => { setModal(null); setDayDetail(null); fetchAssignments(); };

  const weekdayLabels = useMemo(
    () => days?.slice(0, 7)?.map((d) => format(d, 'EEEEEE', { locale: dateLocale })),
    [days, dateLocale]
  );

  // ── Etat vide : aucun travailleur dans l'organisation ───────────────────────
  // « Aucune personne a planifier » : ni travailleur, ni surintendant.
  const noWorkers = !loading && (workers || [])?.length === 0;

  return (
    <div className="pb-12">
      {/* En-tete */}
      <div className="flex items-start justify-between gap-6 mb-6">
        <div className="flex-shrink-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Icon name="CalendarDays" size={20} className="text-primary" />
            <h1 className="text-lg font-semibold text-[#2C3E50]">{t('teamSchedule.title')}</h1>
          </div>
          <p className="text-xs text-gray-500">{t('teamSchedule.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <StatsCard title={t('teamSchedule.stats.people')} value={String(stats?.workers)} iconName="Users" />
          <StatsCard title={t('teamSchedule.stats.personDays')} value={String(stats?.personDays)} iconName="CalendarCheck" />
          <StatsCard title={t('teamSchedule.stats.projects')} value={String(stats?.projects)} iconName="Building2" />
          <StatsCard title={t('teamSchedule.stats.unassigned')} value={String(stats?.unassigned)} valueColor="text-primary" />
        </div>
      </div>

      {/* Barre de controle */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <button
              onClick={() => step(-1)}
              title={t('teamSchedule.previousMonth')}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <span className="text-sm font-semibold text-[#2C3E50] first-letter:uppercase min-w-[150px] text-center">
              {periodLabel}
            </span>
            <button
              onClick={() => step(1)}
              title={t('teamSchedule.nextMonth')}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
          <button
            onClick={goToday}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('teamSchedule.today')}
          </button>
          <div className="w-52">
            <SearchableSelect
              options={[
                { value: 'all', label: t('teamSchedule.allProjects') },
                ...(projects || [])?.map((p) => ({
                  value: p?.id,
                  label: p?.projectNumber ? `${p?.projectNumber} · ${p?.name}` : p?.name,
                })),
              ]}
              value={filterProject}
              onChange={(v) => setFilterProject(v || 'all')}
              size="sm"
            />
          </div>
          <div className="w-44">
            <SearchableSelect
              options={[
                { value: 'all', label: t('teamSchedule.allTrades') },
                ...(trades || [])?.map((tr) => ({ value: tr?.id, label: tr?.name })),
              ]}
              value={filterTrade}
              onChange={(v) => setFilterTrade(v || 'all')}
              size="sm"
            />
          </div>
          {/* Mois / Semaine — un basculeur, pas une liste : les deux choix restent
              visibles. Meme hauteur et meme rayon que les deux filtres a gauche. */}
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            {['month', 'week']?.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setViewMode(m)}
                className={`px-3 py-1.5 text-xs transition-colors ${
                  viewMode === m
                    ? 'bg-orange-50 text-primary font-semibold'
                    : 'bg-white text-gray-600 font-medium hover:bg-gray-50'
                }`}
              >
                {t(`teamSchedule.view.${m}`)}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={() => setModal({ assignment: null, defaultDate: toISODate(new Date()) })}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
        >
          <Icon name="Plus" size={14} />
          {t('teamSchedule.assign')}
        </button>
      </div>

      {/* Legende — une pastille par chantier visible dans le mois */}
      {legend?.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap mb-3 px-0.5">
          {legend?.map((l) => (
            <span key={l?.id} className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: l?.color?.dot }} />
              {l?.label}
            </span>
          ))}
        </div>
      )}

      {noWorkers ? (
        <div className="bg-white border border-gray-200 rounded-xl px-6 py-14 text-center">
          <div className="w-11 h-11 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-3">
            <Icon name="Users" size={20} className="text-primary" />
          </div>
          <p className="text-sm font-medium text-[#2C3E50] mb-1">{t('teamSchedule.empty.title')}</p>
          <p className="text-xs text-gray-500 max-w-sm mx-auto leading-relaxed">{t('teamSchedule.empty.message')}</p>
        </div>
      ) : viewMode === 'week' ? (
        <WeekView
          days={days?.map((d) => ({ date: d, iso: toISODate(d) }))}
          byDate={byDate}
          workerMap={workerMap}
          projectMap={projectMap}
          colorOf={colorOf}
          onSelect={(a) => setModal({ assignment: a, defaultDate: null })}
          onCreate={(iso, time) => setModal({ assignment: null, defaultDate: iso, defaultStart: time })}
        />
      ) : (
        <div className="overflow-x-auto pb-1">
          {/* « 7:00-15:00 » est plus large que « JOUR » : la grille passe de 760 a
              1080px pour que trois puces tiennent sans rogner l'heure. Le conteneur
              defile deja horizontalement, et 1080px entre dans un ecran de 1440. */}
          <div className="min-w-[1080px]">
            {/* En-tete des colonnes */}
            <div className="grid grid-cols-7 gap-1.5 mb-1.5">
              {weekdayLabels?.map((label, i) => (
                <div
                  key={i}
                  className={`text-[10px] font-semibold uppercase tracking-wide text-center py-1 ${
                    i >= 5 ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  {label}
                </div>
              ))}
            </div>

            {/* Les cases */}
            <div className="grid grid-cols-7 gap-1.5">
              {days?.map((day) => {
                const iso = toISODate(day);
                const inMonth = isSameMonth(day, cursor);
                const weekend = isWeekend(day);
                const today = isToday(day);
                const items = byDate?.[iso] || [];
                const shown = items?.slice(0, 3);
                const extra = items?.length - shown?.length;

                return (
                  <div
                    key={iso}
                    onClick={() => setModal({ assignment: null, defaultDate: iso })}
                    className={`group min-h-[104px] rounded-lg border p-1.5 cursor-pointer transition-colors ${
                      inMonth ? 'bg-white border-gray-200' : 'bg-gray-50/70 border-gray-100'
                    } ${weekend && inMonth ? 'bg-slate-50/60' : ''} hover:border-primary/40`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      {today ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold">
                          {format(day, 'd')}
                        </span>
                      ) : (
                        <span className={`text-[11px] font-medium px-0.5 ${inMonth ? 'text-gray-600' : 'text-gray-300'}`}>
                          {format(day, 'd')}
                        </span>
                      )}
                    </div>

                    <div className="space-y-1">
                      {shown?.map((a) => {
                        const c = colorOf(a?.projectId);
                        const p = projectMap?.[a?.projectId];
                        return (
                          <button
                            key={a?.id}
                            onClick={(e) => { e?.stopPropagation(); setModal({ assignment: a, defaultDate: null }); }}
                            title={a?.note || undefined}
                            className="w-full flex items-stretch rounded overflow-hidden text-left hover:brightness-95 transition-all"
                            style={{ backgroundColor: c?.bg }}
                          >
                            <span className="w-[3px] flex-shrink-0" style={{ backgroundColor: c?.bar }} />
                            <span className="flex-1 min-w-0 flex items-center justify-between gap-1 px-1.5 py-1">
                              <span className="text-[10px] font-medium truncate" style={{ color: c?.text }}>
                                {shortName(workerMap?.[a?.workerId]?.full_name)}
                                {p?.projectNumber ? ` · ${p?.projectNumber}` : ''}
                              </span>
                              <span className="text-[9px] font-bold uppercase flex-shrink-0" style={{ color: c?.text }}>
                                {timeLabel(a)}
                              </span>
                            </span>
                          </button>
                        );
                      })}

                      {extra > 0 && (
                        <button
                          onClick={(e) => { e?.stopPropagation(); setDayDetail(iso); }}
                          className="w-full text-[10px] text-gray-400 hover:text-gray-600 text-left px-1.5 transition-colors"
                        >
                          {t('teamSchedule.moreOthers', { count: extra })}
                        </button>
                      )}

                      {items?.length === 0 && inMonth && (
                        <span className="hidden group-hover:flex items-center justify-center gap-1 text-[10px] text-gray-400 border border-dashed border-gray-300 rounded py-1">
                          <Icon name="Plus" size={10} />
                          {t('teamSchedule.addHere')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Detail d'une journee — ouvert par « +N autres » */}
      {dayDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setDayDetail(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-[#2C3E50] first-letter:uppercase">
                {formatDateWeekday(dayDetail, language)}
              </h2>
              <button onClick={() => setDayDetail(null)} className="text-gray-400 hover:text-gray-600">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-1.5">
              {(byDate?.[dayDetail] || [])?.map((a) => {
                const c = colorOf(a?.projectId);
                const p = projectMap?.[a?.projectId];
                return (
                  <button
                    key={a?.id}
                    onClick={() => { setDayDetail(null); setModal({ assignment: a, defaultDate: null }); }}
                    className="w-full flex items-stretch rounded overflow-hidden text-left hover:brightness-95 transition-all"
                    style={{ backgroundColor: c?.bg }}
                  >
                    <span className="w-[3px] flex-shrink-0" style={{ backgroundColor: c?.bar }} />
                    <span className="flex-1 min-w-0 flex items-center justify-between gap-2 px-2.5 py-2">
                      <span className="text-xs font-medium truncate" style={{ color: c?.text }}>
                        {workerMap?.[a?.workerId]?.full_name || '—'}
                        {p?.projectNumber ? ` · ${p?.projectNumber}` : ''}
                      </span>
                      <span className="text-[10px] font-bold uppercase flex-shrink-0" style={{ color: c?.text }}>
                        {timeLabel(a)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {modal && (
        <AssignmentModal
          isOpen
          key={modal?.assignment?.id || modal?.defaultDate || 'new'}
          assignment={modal?.assignment}
          defaultDate={modal?.defaultDate}
          defaultStart={modal?.defaultStart}
          workers={workers}
          projects={projects}
          trades={trades}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

export default TeamSchedulePage;
